'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import * as RechartsPrimitive from 'recharts';
import { tv } from 'tailwind-variants';
import type { LegendPayload } from 'recharts/types/component/DefaultLegendContent';
import type { Formatter, NameType, Payload, ValueType } from 'recharts/types/component/DefaultTooltipContent';

// Format: { THEME_NAME: CSS_SELECTOR }
const THEMES = {
  light: '',
  dark: '.dark'
} as const;
const chartContainer = tv({
  base: `
    [&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground
    [&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-border/50
    [&_.recharts-curve.recharts-tooltip-cursor]:stroke-border
    [&_.recharts-polar-grid_[stroke='#ccc']]:stroke-border
    [&_.recharts-radial-bar-background-sector]:fill-muted
    [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-muted
    [&_.recharts-reference-line_[stroke='#ccc']]:stroke-border
    flex
    aspect-video
    justify-center
    text-xs
    [&_.recharts-dot[stroke='#fff']]:stroke-transparent
    [&_.recharts-layer]:outline-hidden
    [&_.recharts-sector]:outline-hidden
    [&_.recharts-sector[stroke='#fff']]:stroke-transparent
    [&_.recharts-surface]:outline-hidden
  `
});
const chartTooltipContent = tv({
  base: `
    border-border/50
    bg-background
    grid
    min-w-[8rem]
    items-start
    gap-1.5
    rounded-lg
    border
    px-2.5
    py-1.5
    text-xs
    shadow-xl
  `
});
const chartTooltipLabel = tv({
  base: 'font-medium'
});
const chartTooltipItem = tv({
  base: `
    [&>svg]:text-muted-foreground
    flex
    w-full
    flex-wrap
    items-stretch
    gap-2
    [&>svg]:h-2.5
    [&>svg]:w-2.5
  `,
  variants: {
    indicator: {
      dot: 'items-center',
      line: '',
      dashed: ''
    }
  }
});
const chartTooltipIndicator = tv({
  base: 'shrink-0 rounded-[2px] border-(--color-border) bg-(--color-bg)',
  variants: {
    indicator: {
      dot: 'h-2.5 w-2.5',
      line: 'w-1',
      dashed: 'w-0 border-[1.5px] border-dashed bg-transparent'
    },
    nestLabel: {
      true: '',
      false: ''
    }
  },
  compoundVariants: [{
    indicator: 'dashed',
    nestLabel: true,
    className: 'my-0.5'
  }]
});
const chartTooltipValue = tv({
  base: 'flex flex-1 justify-between leading-none',
  variants: {
    nestLabel: {
      true: 'items-end',
      false: 'items-center'
    }
  }
});
const chartLegendContainer = tv({
  base: 'flex items-center justify-center gap-4',
  variants: {
    verticalAlign: {
      top: 'pb-3',
      middle: '',
      bottom: 'pt-3'
    }
  },
  defaultVariants: {
    verticalAlign: 'bottom'
  }
});
const chartLegendItem = tv({
  base: '[&>svg]:text-muted-foreground flex items-center gap-1.5 [&>svg]:h-3 [&>svg]:w-3'
});
export type ChartConfig = { [k in string]: {
  label?: React.ReactNode;
  icon?: React.ComponentType;
} & ({
  color?: string;
  theme?: never;
} | {
  color?: never;
  theme: Record<keyof typeof THEMES, string>;
}) };
type ChartContextProps = {
  config: ChartConfig;
};
const ChartContext = React.createContext<ChartContextProps | null>(null);
function useChart() {
  const context = React.useContext(ChartContext);
  if (!context) {
    throw new Error("useChart must be used within a <ChartContainer />");
  }
  return context;
}
type ChartContainerProps = Omit<React.ComponentProps<'div'>, 'onResize'> & {
  config: ChartConfig;
  children: React.ComponentProps<typeof RechartsPrimitive.ResponsiveContainer>['children'];
  onResize?: React.ComponentProps<typeof RechartsPrimitive.ResponsiveContainer>['onResize'];
};
function ChartContainer(t0) {
  const $ = _c(29);
  let children;
  let className;
  let config;
  let id;
  let onResize;
  let props;
  if ($[0] !== t0) {
    ({
      id,
      className,
      children,
      config,
      onResize,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = config;
    $[4] = id;
    $[5] = onResize;
    $[6] = props;
  } else {
    children = $[1];
    className = $[2];
    config = $[3];
    id = $[4];
    onResize = $[5];
    props = $[6];
  }
  const uniqueId = React.useId();
  let t1;
  if ($[7] !== id || $[8] !== uniqueId) {
    t1 = id || uniqueId.replace(/:/g, "");
    $[7] = id;
    $[8] = uniqueId;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const chartId = `chart-${t1}`;
  let t2;
  if ($[10] !== config) {
    t2 = {
      config
    };
    $[10] = config;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== className) {
    t3 = chartContainer({
      className
    });
    $[12] = className;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== chartId || $[15] !== config) {
    t4 = <ChartStyle config={config} id={chartId} />;
    $[14] = chartId;
    $[15] = config;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== children || $[18] !== onResize) {
    t5 = <RechartsPrimitive.ResponsiveContainer onResize={onResize}>{children}</RechartsPrimitive.ResponsiveContainer>;
    $[17] = children;
    $[18] = onResize;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== chartId || $[21] !== props || $[22] !== t3 || $[23] !== t4 || $[24] !== t5) {
    t6 = <div className={t3} data-chart={chartId} data-slot="chart" {...props}>{t4}{t5}</div>;
    $[20] = chartId;
    $[21] = props;
    $[22] = t3;
    $[23] = t4;
    $[24] = t5;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  let t7;
  if ($[26] !== t2 || $[27] !== t6) {
    t7 = <ChartContext.Provider value={t2}>{t6}</ChartContext.Provider>;
    $[26] = t2;
    $[27] = t6;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  return t7;
}
type ChartStyleProps = {
  id: string;
  config: ChartConfig;
};
function ChartStyle(t0) {
  const $ = _c(8);
  const {
    id,
    config
  } = t0;
  let t1;
  if ($[0] !== config) {
    t1 = Object.entries(config).filter(_temp);
    $[0] = config;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const colorConfig = t1;
  if (!colorConfig.length) {
    return null;
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = Object.entries(THEMES);
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== colorConfig || $[4] !== id) {
    t3 = t2.map(t4 => {
      const [theme, prefix] = t4;
      return `
${prefix} [data-chart=${id}] {
${colorConfig.map(t5 => {
        const [key, itemConfig] = t5;
        const color = itemConfig.theme?.[theme as keyof typeof itemConfig.theme] || itemConfig.color;
        return color ? `  --color-${key}: ${color};` : null;
      }).join("\n")}
}
`;
    });
    $[3] = colorConfig;
    $[4] = id;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = t3.join("\n");
  let t5;
  if ($[6] !== t4) {
    t5 = <style dangerouslySetInnerHTML={{
      __html: t4
    }} />;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}
function _temp(t0) {
  const [, config_0] = t0;
  return config_0.theme || config_0.color;
}
const ChartTooltip = RechartsPrimitive.Tooltip;
type ChartTooltipContentProps = React.ComponentProps<typeof RechartsPrimitive.Tooltip> & React.ComponentProps<'div'> & {
  active?: boolean;
  payload?: readonly Payload<ValueType, NameType>[];
  label?: unknown;
  labelFormatter?: (label: unknown, payload: readonly Payload<ValueType, NameType>[]) => React.ReactNode;
  labelClassName?: string;
  formatter?: Formatter<ValueType, NameType>;
  color?: string;
  hideLabel?: boolean;
  hideIndicator?: boolean;
  indicator?: 'line' | 'dot' | 'dashed';
  nameKey?: string;
  labelKey?: string;
};
function ChartTooltipContent(t0) {
  const $ = _c(45);
  const {
    active,
    payload,
    className,
    indicator: t1,
    hideLabel: t2,
    hideIndicator: t3,
    label,
    labelFormatter,
    labelClassName,
    formatter,
    color,
    nameKey,
    labelKey
  } = t0;
  const indicator = t1 === undefined ? "dot" : t1;
  const hideLabel = t2 === undefined ? false : t2;
  const hideIndicator = t3 === undefined ? false : t3;
  const {
    config
  } = useChart();
  let t4;
  bb0: {
    if (hideLabel || !payload?.length) {
      t4 = null;
      break bb0;
    }
    const [item] = payload;
    const key = `${labelKey || item?.dataKey || item?.name || "value"}`;
    let t5;
    if ($[0] !== config || $[1] !== item || $[2] !== key) {
      t5 = getPayloadConfigFromPayload(config, item, key);
      $[0] = config;
      $[1] = item;
      $[2] = key;
      $[3] = t5;
    } else {
      t5 = $[3];
    }
    const itemConfig = t5;
    const value = !labelKey && typeof label === "string" ? config[label as keyof typeof config]?.label || label : itemConfig?.label;
    if (labelFormatter) {
      let t6;
      if ($[4] !== labelClassName) {
        t6 = chartTooltipLabel({
          className: labelClassName
        });
        $[4] = labelClassName;
        $[5] = t6;
      } else {
        t6 = $[5];
      }
      let t7;
      if ($[6] !== labelFormatter || $[7] !== payload || $[8] !== value) {
        t7 = labelFormatter(value, payload);
        $[6] = labelFormatter;
        $[7] = payload;
        $[8] = value;
        $[9] = t7;
      } else {
        t7 = $[9];
      }
      let t8;
      if ($[10] !== t6 || $[11] !== t7) {
        t8 = <div className={t6}>{t7}</div>;
        $[10] = t6;
        $[11] = t7;
        $[12] = t8;
      } else {
        t8 = $[12];
      }
      t4 = t8;
      break bb0;
    }
    if (!value) {
      t4 = null;
      break bb0;
    }
    let t6;
    if ($[13] !== labelClassName) {
      t6 = chartTooltipLabel({
        className: labelClassName
      });
      $[13] = labelClassName;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    let t7;
    if ($[15] !== t6 || $[16] !== value) {
      t7 = <div className={t6}>{value}</div>;
      $[15] = t6;
      $[16] = value;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    t4 = t7;
  }
  const tooltipLabel = t4;
  if (!active || !payload?.length) {
    return null;
  }
  const nestLabel = payload.length === 1 && indicator !== "dot";
  let t5;
  if ($[18] !== className) {
    t5 = chartTooltipContent({
      className
    });
    $[18] = className;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const t6 = !nestLabel ? tooltipLabel : null;
  let t7;
  if ($[20] !== color || $[21] !== config || $[22] !== formatter || $[23] !== hideIndicator || $[24] !== indicator || $[25] !== nameKey || $[26] !== nestLabel || $[27] !== payload || $[28] !== tooltipLabel) {
    let t8;
    if ($[30] !== color || $[31] !== config || $[32] !== formatter || $[33] !== hideIndicator || $[34] !== indicator || $[35] !== nameKey || $[36] !== nestLabel || $[37] !== tooltipLabel) {
      t8 = (item_0, index) => {
        const key_0 = `${nameKey || item_0.name || item_0.dataKey || "value"}`;
        const itemConfig_0 = getPayloadConfigFromPayload(config, item_0, key_0);
        const indicatorColor = color || item_0.payload.fill || item_0.color;
        return <div className={chartTooltipItem({
          indicator
        })} key={index}>{formatter && item_0?.value !== undefined && item_0.name ? formatter(item_0.value, item_0.name, item_0, index, item_0.payload) : <>{itemConfig_0?.icon ? <itemConfig_0.icon /> : !hideIndicator && <div className={chartTooltipIndicator({
              indicator,
              nestLabel
            })} style={{
              "--color-bg": indicatorColor,
              "--color-border": indicatorColor
            } as React.CSSProperties} />}<div className={chartTooltipValue({
              nestLabel
            })}><div className="grid gap-1.5">{nestLabel ? tooltipLabel : null}<span className="text-muted-foreground">{itemConfig_0?.label || item_0.name}</span></div>{item_0.value && <span className="text-foreground font-mono font-medium tabular-nums">{item_0.value.toLocaleString()}</span>}</div></>}</div>;
      };
      $[30] = color;
      $[31] = config;
      $[32] = formatter;
      $[33] = hideIndicator;
      $[34] = indicator;
      $[35] = nameKey;
      $[36] = nestLabel;
      $[37] = tooltipLabel;
      $[38] = t8;
    } else {
      t8 = $[38];
    }
    t7 = payload.map(t8);
    $[20] = color;
    $[21] = config;
    $[22] = formatter;
    $[23] = hideIndicator;
    $[24] = indicator;
    $[25] = nameKey;
    $[26] = nestLabel;
    $[27] = payload;
    $[28] = tooltipLabel;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  let t8;
  if ($[39] !== t7) {
    t8 = <div className="grid gap-1.5">{t7}</div>;
    $[39] = t7;
    $[40] = t8;
  } else {
    t8 = $[40];
  }
  let t9;
  if ($[41] !== t5 || $[42] !== t6 || $[43] !== t8) {
    t9 = <div className={t5}>{t6}{t8}</div>;
    $[41] = t5;
    $[42] = t6;
    $[43] = t8;
    $[44] = t9;
  } else {
    t9 = $[44];
  }
  return t9;
}
const ChartLegend = RechartsPrimitive.Legend;
type ChartLegendContentProps = React.ComponentProps<'div'> & {
  payload?: LegendPayload[];
  verticalAlign?: 'top' | 'middle' | 'bottom';
  hideIcon?: boolean;
  nameKey?: string;
};
function ChartLegendContent(t0) {
  const $ = _c(15);
  const {
    className,
    hideIcon: t1,
    payload,
    verticalAlign: t2,
    nameKey
  } = t0;
  const hideIcon = t1 === undefined ? false : t1;
  const verticalAlign = t2 === undefined ? "bottom" : t2;
  const {
    config
  } = useChart();
  if (!payload?.length) {
    return null;
  }
  let t3;
  if ($[0] !== className || $[1] !== verticalAlign) {
    t3 = chartLegendContainer({
      verticalAlign,
      className
    });
    $[0] = className;
    $[1] = verticalAlign;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== config || $[4] !== hideIcon || $[5] !== nameKey || $[6] !== payload) {
    let t5;
    if ($[8] !== config || $[9] !== hideIcon || $[10] !== nameKey) {
      t5 = item => {
        const key = `${nameKey || item.dataKey || "value"}`;
        const itemConfig = getPayloadConfigFromPayload(config, item, key);
        return <div className={chartLegendItem()} key={item.value}>{itemConfig?.icon && !hideIcon ? <itemConfig.icon /> : <div className="h-2 w-2 shrink-0 rounded-[2px]" style={{
            backgroundColor: item.color
          }} />}{itemConfig?.label}</div>;
      };
      $[8] = config;
      $[9] = hideIcon;
      $[10] = nameKey;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    t4 = payload.map(t5);
    $[3] = config;
    $[4] = hideIcon;
    $[5] = nameKey;
    $[6] = payload;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[12] !== t3 || $[13] !== t4) {
    t5 = <div className={t3}>{t4}</div>;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}

// Helper to extract item config from a payload.
function getPayloadConfigFromPayload(config: ChartConfig, payload: unknown, key: string) {
  if (typeof payload !== 'object' || payload === null) {
    return undefined;
  }
  const payloadPayload = 'payload' in payload && typeof payload.payload === 'object' && payload.payload !== null ? payload.payload : undefined;
  let configLabelKey: string = key;
  if (key in payload && typeof payload[key as keyof typeof payload] === 'string') {
    configLabelKey = payload[key as keyof typeof payload] as string;
  } else if (payloadPayload && key in payloadPayload && typeof payloadPayload[key as keyof typeof payloadPayload] === 'string') {
    configLabelKey = payloadPayload[key as keyof typeof payloadPayload] as string;
  }
  return configLabelKey in config ? config[configLabelKey] : config[key as keyof typeof config];
}
export { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent, ChartStyle, useChart, chartContainer, chartTooltipContent, chartTooltipLabel, chartTooltipItem, chartTooltipIndicator, chartTooltipValue, chartLegendContainer, chartLegendItem, type ChartContainerProps, type ChartTooltipContentProps, type ChartLegendContentProps, type ChartStyleProps };